const KEY_PREFIX = "snk:";
const VALUE_PREFIX = "snv:";

let installed = false;

const toBase64 = (input: string): string => {
  const bytes = new TextEncoder().encode(input);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
};

const fromBase64 = (input: string): string => {
  const binary = atob(input);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

const encodeKey = (key: string) => `${KEY_PREFIX}${toBase64(key)}`;
const encodeValue = (value: string) => `${VALUE_PREFIX}${toBase64(value)}`;

const decodeKeyIfNeeded = (key: string): string => {
  if (!key.startsWith(KEY_PREFIX)) return key;
  try {
    return fromBase64(key.slice(KEY_PREFIX.length));
  } catch {
    return key;
  }
};

const decodeValueIfNeeded = (value: string): string => {
  if (!value.startsWith(VALUE_PREFIX)) return value;
  try {
    return fromBase64(value.slice(VALUE_PREFIX.length));
  } catch {
    return value;
  }
};

export const installEncodedLocalStorage = () => {
  if (installed || typeof window === "undefined" || typeof Storage === "undefined") {
    return;
  }

  installed = true;

  const proto = Storage.prototype;
  const originalGetItem = proto.getItem;
  const originalSetItem = proto.setItem;
  const originalRemoveItem = proto.removeItem;
  const originalKey = proto.key;

  proto.getItem = function getItem(key: string): string | null {
    const encodedKey = encodeKey(key);
    const encodedValue = originalGetItem.call(this, encodedKey);
    if (encodedValue != null) {
      return decodeValueIfNeeded(encodedValue);
    }

    // Backward compatibility for previously stored plain keys.
    const plainValue = originalGetItem.call(this, key);
    if (plainValue == null) return null;
    return decodeValueIfNeeded(plainValue);
  };

  proto.setItem = function setItem(key: string, value: string): void {
    const encodedKey = encodeKey(key);
    const encodedValue = encodeValue(String(value));
    originalSetItem.call(this, encodedKey, encodedValue);

    // Cleanup any legacy plain-key value to avoid duplicates.
    if (encodedKey !== key) {
      originalRemoveItem.call(this, key);
    }
  };

  proto.removeItem = function removeItem(key: string): void {
    const encodedKey = encodeKey(key);
    originalRemoveItem.call(this, encodedKey);
    originalRemoveItem.call(this, key);
  };

  proto.key = function key(index: number): string | null {
    const value = originalKey.call(this, index);
    if (value == null) return null;
    return decodeKeyIfNeeded(value);
  };
};

